<template>
  <div>
    <h2><v-icon>mdi-filter</v-icon> Refine results</h2>
    <p class="few-patients-error" v-if="fewPatients">
      Cannot display fewer than 2 patients. Please expand your filtering
      criteria.
    </p>
    <v-row>
      <v-col cols="12" sm="8" md="6">
        <v-subheader>Age at assessment*</v-subheader>
        <v-range-slider
          v-model="ageRange"
          :disabled="ageRangeDisabled"
          max="95"
          min="20"
          hide-details
          class="align-center pt-3"
          step="5"
          @change="filtersChanged()"
        >
          <template v-slot:prepend>
            <div class="pa-3">
              {{ ageRange[0] }}
            </div>
          </template>
          <template v-slot:append>
            <div class="pa-3">
              {{ ageRange[1] }}
            </div>
          </template>
        </v-range-slider>
      </v-col>
      <v-col cols="12" sm="4" md="3">
        <v-subheader>Sex</v-subheader>
        <v-select
          v-model="sex"
          :items="sexPossible"
          label="Sex"
          single-line
          @change="filtersChanged()"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="4" md="3">
        <v-subheader>Codon 129</v-subheader>
        <v-select
          v-model="codon"
          :items="codonPossible"
          label="Codon 129"
          single-line
          @change="filtersChanged()"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="4" md="3">
        <v-subheader>Diagnosis</v-subheader>
        <v-select
          v-model="diagnosis"
          :items="diagnosisPossible"
          label="Diagnosis"
          single-line
        ></v-select>
      </v-col>
      <v-col cols="12" sm="4" md="3">
        <v-subheader>IPD Mutation</v-subheader>
        <v-select
          :disabled="mutationDisabled"
          v-model="mutation"
          :items="mutationPossible"
          label="IPD Mutation"
          single-line
          @change="filtersChanged()"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="8" md="6">
        <v-subheader>Baseline MRC score*</v-subheader>
        <v-range-slider
          v-model="baselineScore"
          max="20"
          min="0"
          hide-details
          class="align-center pt-3"
          step="1"
          @change="filtersChanged()"
        >
          <template v-slot:prepend>
            <div class="pa-3">
              {{ baselineScore[0] }}
            </div>
          </template>
          <template v-slot:append>
            <div class="pa-3">
              {{ baselineScore[1] }}
            </div>
          </template>
        </v-range-slider>
      </v-col>
      <!--<v-col cols="12" sm="4" md="3">-->
      <!--  <v-subheader>PrP(Sc) Type</v-subheader>-->
      <!--  <v-select-->
      <!--    v-model="prpType"-->
      <!--    :items="prpTypePossible"-->
      <!--    label="PrP(Sc) Type"-->
      <!--    single-line-->
      <!--    @change="filtersChanged()"-->
      <!--  ></v-select>-->
      <!--</v-col>-->
    </v-row>
    <p>* Range inclusive at both extreme values.</p>
    <v-row>
      <v-col>
        <v-subheader>Filtering functionality</v-subheader>
        <v-radio-group v-model="highlightGroup" class="mt-1">
          <v-radio
            key="1"
            label="Show only filtered results"
            :value="false"
          ></v-radio>
          <v-radio
            key="2"
            label="Highlight filtered results"
            :value="true"
          ></v-radio>
        </v-radio-group>
      </v-col>
    </v-row>
    <v-btn outlined color="accent" @click="resetFilters"
      ><v-icon class="mr-2">mdi-backup-restore</v-icon>Reset filters</v-btn
    >
  </div>
</template>

<style scoped>
.v-subheader {
  font-size: 1rem;
  padding-left: 0;
}

.few-patients-error {
  color: var(--v-error-base);
  margin-top: 16px;
  margin-bottom: 0;
}
</style>

<script>
export default {
  props: {
    fewPatients: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    ageRange: [20, 95],
    ageRangeDisabled: false,
    baselineScore: [0, 20],
    sex: null,
    sexPossible: [
      { text: "Any", value: null },
      { text: "Male", value: true },
      { text: "Female", value: false }
    ],
    codon: null,
    codonPossible: [
      { text: "Any", value: null },
      { text: "MM", value: 1 },
      { text: "MV", value: 2 },
      { text: "VV", value: 3 }
    ],
    diagnosis: null,
    mutation: null,
    mutationDisabled: true,
    // prpType: null,
    // prpTypePossible: [{ text: "Any", value: null }, 1, 2],
    highlightGroup: false
  }),
  computed: {
    diagnosisPossible() {
      switch (this.$store.state.modelGroupTab + 1) {
        case 1:
          return [{ text: "Any", value: null }, "sCJD", "IPD"];
        case 2:
          return [{ text: "Any", value: null }, "vCJD", "IPD"];
        case 3:
          return ["iCJD"];
        default:
          return [{ text: "Any", value: null }];
      }
    },
    mutationPossible() {
      switch (this.$store.state.modelGroupTab + 1) {
        case 1:
          return [
            { text: "Any", value: null },
            "4OPRI",
            "D178N",
            "E200K",
            "E211Q",
            "E196Q",
            "V210I"
          ];
        case 2:
          return [
            { text: "Any", value: null },
            "P102L",
            "5OPRI",
            "6OPRI",
            "8OPRI",
            "A117V",
            "P105L",
            "Y163X",
            "Q212P",
            "E196K"
          ];
        default:
          return [{ text: "Any", value: null }];
      }
    }
  },
  watch: {
    diagnosis: function(value) {
      // Enable the mutation filter
      if (value === "IPD") {
        this.mutationDisabled = false;
        this.filtersChanged();
      } else {
        this.mutationDisabled = true;
        this.mutation = null;
        this.filtersChanged();
      }
    },
    highlightGroup: function(value) {
      if (value) {
        // Reset and disable age range
        this.ageRange = [20, 95];
        this.ageRangeDisabled = true;
        this.filtersChanged();
      } else {
        this.ageRangeDisabled = false;
        this.filtersChanged();
      }
    },
    "$store.state.modelGroupTab": async function(value) {
      // Change the axisMax auto.
      if (value === 0 || value === 2) {
        this.$store.state.axisMaxAuto = false;
        this.$store.state.axisMax = 365;
      } else {
        this.$store.state.axisMaxAuto = false;
        this.$store.state.axisMax = 1000;
      }

      await this.$nextTick();
      // Reset other filters and emit the event
      this.resetFilters();
    }
  },
  methods: {
    filtersChanged() {
      let newFilters = {
        ageMin: this.ageRange[0],
        ageMax: this.ageRange[1],
        baseline20Min: this.baselineScore[0],
        baseline20Max: this.baselineScore[1],
        isMale: this.sex,
        geneticsCode: this.codon,
        diagnosis: this.diagnosis,
        IPDMutation: this.mutation,
        // prpType: this.prpType,
        highlightGroup: this.highlightGroup,
        modelGroup: this.$store.state.modelGroupTab + 1
      };
      this.$emit("filtersChanged", newFilters);
    },
    resetFilters() {
      // Reset filters
      this.ageRange = [20, 95];
      this.baselineScore = [0, 20];
      this.sex = null;
      this.codon = null;
      this.diagnosis = null;
      this.mutation = null;
      // this.prpType = null;
      this.highlightGroup = false;

      // Trigger the event
      this.filtersChanged();
    }
  },
  created() {
    this.filtersChanged();
  }
};
</script>
